import { useScrollReveal } from '../hooks/useScrollReveal';
import { useParallax } from '../hooks/useParallax';

const coreFeatures = [
  { label: '本课摘要', icon: 'ri-article-line', color: 'primary' as const, desc: '快速回顾课堂要点' },
  { label: '核心概念', icon: 'ri-lightbulb-line', color: 'accent' as const, desc: '提炼关键知识节点' },
  { label: '重点视频片段', icon: 'ri-play-circle-line', color: 'primary' as const, desc: '标记精彩教学时刻' },
];

const supportFeatures = [
  { label: '课后测验', icon: 'ri-edit-2-line', color: 'accent' as const, desc: '即时检测理解程度' },
  { label: '常见疑问', icon: 'ri-chat-3-line', color: 'primary' as const, desc: '汇总学生反馈难点' },
];

export default function P63ServiceExpert({ forceVisible = false }: { forceVisible?: boolean }) {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal(0.12, forceVisible);
  const { ref: orb1Ref, offset: orb1Y } = useParallax(0.05);
  const { ref: orb2Ref, offset: orb2Y } = useParallax(-0.04);

  return (
    <div className="relative h-screen flex flex-col justify-center overflow-hidden">
      {/* 三层背景光效 */}
      <div ref={orb1Ref} className="absolute top-20 right-24 w-[380px] h-[380px] rounded-full bg-primary-200/30 blur-[120px] pointer-events-none" style={{ transform: `translateY(${orb1Y}px)` }} />
      <div ref={orb2Ref} className="absolute bottom-28 left-12 w-[400px] h-[400px] rounded-full bg-accent-200/25 blur-[110px] pointer-events-none" style={{ transform: `translateY(${orb2Y}px)` }} />
      <div className="absolute top-1/2 left-1/3 w-[300px] h-[300px] rounded-full bg-secondary-200/20 blur-[140px] pointer-events-none animate-orb-float-3" />

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-3 md:py-4">
        <div ref={titleRef} className="text-center mb-2 md:mb-3">
          <span className={`inline-flex items-center gap-2 text-base font-body font-semibold text-gold-600 uppercase tracking-[0.15em] mb-2 transition-all duration-700 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <span className="gold-dot" />服务学生
          </span>
          <h2 className={`font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-2 transition-all duration-1000 delay-100 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            把评课结果转化为<span className="text-gold-600">智能学习支持</span>
          </h2>
          <p className={`text-base text-foreground-950 font-body max-w-2xl mx-auto leading-relaxed transition-all duration-1000 delay-200 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            评课过程中识别出的重点和证据，可以继续成为学生的课堂回看与复习资源
          </p>
        </div>

        {/* Student App Features — 核心资源 + 互动反馈 分组 */}
        <div className={`max-w-4xl mx-auto mb-3 transition-all duration-1000 delay-300 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          <div className="p-4 md:p-5 glass-liquid rounded-2xl">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-3 md:gap-4">
              {/* 左侧 — 核心学习资源（3列，3个横向卡片） */}
              <div className="lg:col-span-3 flex flex-col gap-2">
                <div className="flex items-center gap-2 mb-0.5">
                  <div className="w-1 h-4 rounded-full bg-primary-500" />
                  <span className="text-xs font-body font-semibold text-foreground-950 uppercase tracking-wider">核心学习资源</span>
                </div>
                {coreFeatures.map((f) => (
                  <div
                    key={f.label}
                    className="flex items-center gap-3 p-3 rounded-xl glass border border-background-200/40 hover:border-gold-300/50 transition-all duration-300 cursor-default group"
                  >
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${f.color === 'accent' ? 'bg-gold-100/80 border border-gold-300/40' : 'bg-primary-100/60 border border-primary-200/40'} group-hover:scale-105 transition-transform duration-300`}>
                      <i className={`${f.icon} text-xl ${f.color === 'accent' ? 'text-gold-600' : 'text-primary-600'}`} />
                    </div>
                    <div className="text-left">
                      <span className="text-base font-body font-bold text-foreground-950 block">{f.label}</span>
                      <span className="text-sm text-foreground-950 font-body">{f.desc}</span>
                    </div>
                    <i className="ri-arrow-right-s-line text-foreground-950 ml-auto opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </div>
                ))}
              </div>

              {/* 右侧 — 互动反馈（2列，2个卡片纵向排列） */}
              <div className="lg:col-span-2 flex flex-col gap-2">
                <div className="flex items-center gap-2 mb-0.5">
                  <div className="w-1 h-4 rounded-full bg-gold-400" />
                  <span className="text-xs font-body font-semibold text-foreground-950 uppercase tracking-wider">互动反馈</span>
                </div>
                {supportFeatures.map((f) => (
                  <div
                    key={f.label}
                    className="flex-1 flex flex-col items-center justify-center gap-2 p-3 rounded-xl glass border border-background-200/40 text-center hover:border-gold-300/50 transition-all duration-300 cursor-default group"
                  >
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${f.color === 'accent' ? 'bg-gold-100/80 border border-gold-300/40' : 'bg-primary-100/60 border border-primary-200/40'} group-hover:scale-105 transition-transform duration-300`}>
                      <i className={`${f.icon} text-xl ${f.color === 'accent' ? 'text-gold-600' : 'text-primary-600'}`} />
                    </div>
                    <div>
                      <span className="text-base font-body font-bold text-foreground-950 block">{f.label}</span>
                      <span className="text-sm text-foreground-950 font-body">{f.desc}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Video replay highlight */}
        <div className={`max-w-lg mx-auto mb-3 transition-all duration-1000 delay-500 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="flex items-center justify-center gap-3 p-3 rounded-xl glass-gold border border-gold-200/40">
            <div className="w-11 h-11 rounded-lg bg-gold-100/80 border border-gold-300/40 flex items-center justify-center">
              <i className="ri-links-line text-xl text-gold-600" />
            </div>
            <div>
              <p className="text-base font-body font-bold text-gold-800">点击知识点 → 跳转到对应视频时间</p>
              <p className="text-sm text-gold-600 font-body">不用重新拖动完整视频寻找内容</p>
            </div>
          </div>
        </div>

        <div className={`max-w-3xl mx-auto text-center transition-all duration-1000 delay-700 ${titleVisible ? 'opacity-100' : 'opacity-0'}`}>
          <div className="p-4 glass-liquid rounded-xl">
            <p className="text-base text-foreground-950 font-body leading-relaxed">
              学生的疑问、回看位置和课后反馈还能反向汇总，帮助教师判断哪些内容已经讲清、哪些概念仍然存在理解障碍。<strong className="text-gold-700">课堂评价与学生学习不再是两条分离链路</strong>，而是形成双向连接。
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}